/**
 * /api/system/* — server health + per-card session resource metrics.
 *
 * Phase 12 extends the original /system/load with:
 *   - /system/sessions/:id/load  — live cAdvisor read for a card session
 *   - /system/sessions/active    — multi-session list (founder dashboard)
 *   - /system/sessions/estimate  — pre-launch RAM/CPU estimate + risk
 *
 * Polling cadences (TanStack Query staleTime + refetchInterval):
 *   - load        10s
 *   - session     5s   (Resources tab — real-time feel)
 *   - active     10s   (dashboard widget)
 *   - estimate   N/A   (one-shot per card open)
 */
import type {
  ActiveSessionsResponse,
  SessionEstimate,
  SessionLoad,
  SystemLoad,
} from '@chaos/types/system';

import { apiJson } from './client';

export type { ActiveSessionsResponse, SessionEstimate, SessionLoad, SystemLoad };

export const systemApi = {
  load(): Promise<SystemLoad> {
    return apiJson<SystemLoad>('/system/load');
  },
  sessionLoad(sessionId: string): Promise<SessionLoad> {
    return apiJson<SessionLoad>(`/system/sessions/${sessionId}/load`);
  },
  activeSessions(): Promise<ActiveSessionsResponse> {
    return apiJson<ActiveSessionsResponse>('/system/sessions/active');
  },
  estimate(model: string): Promise<SessionEstimate> {
    return apiJson<SessionEstimate>(`/system/sessions/estimate?model=${encodeURIComponent(model)}`);
  },
};

export const systemKeys = {
  all: ['system'] as const,
  load: () => [...systemKeys.all, 'load'] as const,
  sessionLoad: (sessionId: string) => [...systemKeys.all, 'session', sessionId, 'load'] as const,
  activeSessions: () => [...systemKeys.all, 'sessions', 'active'] as const,
  estimate: (model: string) => [...systemKeys.all, 'sessions', 'estimate', model] as const,
};
